import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError, DUCK_LOGO } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Gauge, Rocket, Sparkles, TrendingUp, Target, Zap, ArrowRight, Radio, Layers,
  Send, Bot, Shield, HeartPulse, Building2, ChevronDown, ChevronUp, CheckCircle2,
  AlertTriangle, DollarSign, Clock, Percent, ScaleIcon, Search, X, ArrowUpRight,
  BarChart3, ChevronRight, FileUp
} from "lucide-react";
import MyCreditReport from "@/components/app/MyCreditReport";

const CATEGORIES = [
  { key: "all",              label: "All" },
  { key: "personal_credit",  label: "Personal Credit" },
  { key: "business_credit",  label: "Business Credit" },
  { key: "sba",              label: "SBA" },
  { key: "equipment",        label: "Equipment" },
  { key: "auto",             label: "Auto" },
  { key: "real_estate",      label: "Real Estate" },
  { key: "line_of_credit",   label: "Lines of Credit" },
  { key: "working_capital",  label: "Working Capital" },
  { key: "startup",          label: "Startups" },
];

const TAGS = [
  { key: "all",                 label: "All tags" },
  { key: "0_apr",               label: "0% APR" },
  { key: "highest_limits",      label: "Highest limits" },
  { key: "fastest_funding",     label: "Fastest funding" },
  { key: "best_approval_odds",  label: "Best approval odds" },
  { key: "no_pg",               label: "No PG" },
  { key: "rewards",             label: "Rewards" },
  { key: "cashback",            label: "Cashback" },
  { key: "travel",              label: "Travel" },
  { key: "starter",             label: "Starter" },
  { key: "premium",             label: "Premium" },
];

const SECTIONS = [
  { key: "dashboard", label: "Command", icon: Gauge },
  { key: "my_report", label: "My Real Report", icon: FileUp },
  { key: "opportunities", label: "Opportunities", icon: Target },
  { key: "concierge", label: "AI Concierge", icon: Bot },
  { key: "simulator", label: "Simulator", icon: BarChart3 },
  { key: "doctor", label: "Credit Doctor", icon: HeartPulse },
  { key: "business", label: "Business Readiness", icon: Building2 },
];

// Small animated number
function AnimatedNumber({ value, prefix = "", suffix = "", duration = 900 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef({ raf: null, from: 0, to: 0, start: 0 });
  useEffect(() => {
    const from = ref.current.to || 0;
    ref.current = { raf: null, from, to: value, start: performance.now() };
    const tick = (t) => {
      const p = Math.min(1, (t - ref.current.start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(ref.current.from + (ref.current.to - ref.current.from) * eased));
      if (p < 1) ref.current.raf = requestAnimationFrame(tick);
    };
    ref.current.raf = requestAnimationFrame(tick);
    return () => ref.current.raf && cancelAnimationFrame(ref.current.raf);
  }, [value, duration]);
  return <span>{prefix}{display.toLocaleString()}{suffix}</span>;
}

// Human-readable label for a 0-100 readiness score
function readinessBand(score) {
  if (score >= 90) return { label: "Elite", color: "#10b981" };
  if (score >= 75) return { label: "Strong", color: "#22c55e" };
  if (score >= 55) return { label: "Fundable", color: "#38bdf8" };
  if (score >= 35) return { label: "Building", color: "#f59e0b" };
  return { label: "Foundation", color: "#f97316" };
}

// Circular progress ring
function ProgressRing({ value = 0, max = 100, size = 120, stroke = 10, label, sublabel, color = "#f59e0b" }) {
  const pct = Math.max(0, Math.min(1, value / max));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct)}
          style={{ transition: "stroke-dashoffset 900ms cubic-bezier(0.33,1,0.68,1)", transform: "rotate(-90deg)", transformOrigin: "50% 50%" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <div className="font-display text-2xl font-black text-white leading-none"><AnimatedNumber value={value} /></div>
        {sublabel && <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mt-0.5">{sublabel}</div>}
      </div>
    </div>
  );
}

/* ============================================================== */
export default function FundingCenter() {
  const initialSection = typeof window !== "undefined" ? (new URLSearchParams(window.location.search).get("section") || "dashboard") : "dashboard";
  const [section, setSection] = useState(initialSection);
  const [dash, setDash] = useState(null);
  const [loadingDash, setLoadingDash] = useState(true);
  const [opps, setOpps] = useState([]);
  const [category, setCategory] = useState("all");
  const [tag, setTag] = useState("all");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [compareIds, setCompareIds] = useState([]);
  const [compareResult, setCompareResult] = useState(null);
  const [issues, setIssues] = useState([]);
  const [biz, setBiz] = useState(null);
  const [openBooking, setOpenBooking] = useState(false);

  const loadDashboard = async () => {
    setLoadingDash(true);
    try {
      const { data } = await api.get("/funding/intel/dashboard");
      setDash(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoadingDash(false); }
  };
  const loadOpps = async () => {
    const params = {};
    if (category !== "all") params.category = category;
    if (tag !== "all") params.tag = tag;
    const { data } = await api.get("/funding/intel/opportunities", { params });
    setOpps(data.opportunities);
  };
  const loadDoctor = async () => {
    const { data } = await api.get("/funding/intel/credit-doctor");
    setIssues(data.issues);
  };
  const loadBiz = async () => {
    const { data } = await api.get("/funding/intel/business-readiness");
    setBiz(data);
  };
  useEffect(() => { loadDashboard(); loadOpps(); loadDoctor(); loadBiz(); }, []);
  useEffect(() => { loadOpps(); /* eslint-disable-next-line */ }, [category, tag]);

  const filteredOpps = useMemo(() => {
    if (!q) return opps;
    const s = q.toLowerCase();
    return opps.filter((o) =>
      o.name.toLowerCase().includes(s) || o.issuer.toLowerCase().includes(s) || o.type.toLowerCase().includes(s));
  }, [opps, q]);

  const toggleExpand = (id) => {
    const n = new Set(expanded);
    n.has(id) ? n.delete(id) : n.add(id);
    setExpanded(n);
  };
  const toggleCompare = (id) => {
    if (compareIds.includes(id)) setCompareIds(compareIds.filter((x) => x !== id));
    else if (compareIds.length < 3) setCompareIds([...compareIds, id]);
    else toast("You can compare up to 3 at a time.");
  };
  const runCompare = async () => {
    try {
      const { data } = await api.post("/funding/intel/compare", { opportunity_ids: compareIds });
      setCompareResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loadingDash || !dash) {
    return <AppShell><div className="min-h-[60vh] flex items-center justify-center text-slate-400">Loading Funding Intelligence…</div></AppShell>;
  }

  return (
    <div className="min-h-screen bg-[#0A0B10] text-slate-100">
      <style>{`
        .cq-dark { color-scheme: dark; }
        .glass { background: rgba(255,255,255,0.04); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border: 1px solid rgba(255,255,255,0.08); }
        .glass-strong { background: rgba(255,255,255,0.06); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); border: 1px solid rgba(255,255,255,0.10); }
        .glow-amber { box-shadow: 0 0 40px rgba(245,158,11,0.15), inset 0 0 40px rgba(245,158,11,0.03); }
      `}</style>

      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full bg-amber-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-[600px] w-[600px] rounded-full bg-sky-500/10 blur-3xl" />
        <div className="absolute top-1/2 left-1/3 h-[400px] w-[400px] rounded-full bg-purple-500/5 blur-3xl" />
      </div>

      <div className="relative">
        {/* Top ticker/header */}
        <header className="border-b border-white/5 backdrop-blur-lg bg-black/40 sticky top-0 z-30" data-testid="intel-header">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <img src={DUCK_LOGO} alt="Credit Quack" className="h-9 w-9 sm:h-11 sm:w-11 object-contain shrink-0 cq-waddle" data-testid="fc-brand-logo" />
              <div className="min-w-0">
                <p className="font-display font-black text-sm sm:text-lg tracking-tight leading-tight truncate">
                  <span className="hidden sm:inline">Funding Intelligence: A CreditQuack App</span>
                  <span className="sm:hidden">Funding Intel</span>
                </p>
                <div className="flex items-center gap-1 text-[9px] sm:text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                  <Radio size={9} className="text-emerald-400 animate-pulse" /> Live
                </div>
              </div>
            </div>
            <a href="/portal" className="text-xs sm:text-sm text-slate-400 hover:text-white transition inline-flex items-center gap-1 shrink-0" data-testid="back-to-portal">
              <span className="hidden sm:inline">← Back to portal</span>
              <span className="sm:hidden">← Back</span>
            </a>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-32">
          {/* Section tabs */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="intel-tabs">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const active = section === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => setSection(s.key)}
                  data-testid={`intel-tab-${s.key}`}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition ${
                    active ? "bg-white text-slate-900 border-white" : "glass text-slate-300 border-white/10 hover:border-white/20"
                  }`}
                >
                  <Icon size={14} /> {s.label}
                </button>
              );
            })}
          </div>

          {section === "dashboard" && <DashboardSection dash={dash} onBook={() => setOpenBooking(true)} onNav={setSection} />}
          {section === "my_report" && <MyCreditReport onGoToOpportunities={() => setSection("opportunities")} />}
          {section === "opportunities" && (
            <OpportunitiesSection
              opps={filteredOpps} category={category} setCategory={setCategory}
              tag={tag} setTag={setTag} q={q} setQ={setQ}
              expanded={expanded} toggleExpand={toggleExpand}
              compareIds={compareIds} toggleCompare={toggleCompare} runCompare={runCompare}
              compareResult={compareResult} clearCompare={() => { setCompareIds([]); setCompareResult(null); }}
              onBook={() => setOpenBooking(true)}
            />
          )}
          {section === "concierge" && <ConciergeSection />}
          {section === "simulator" && (
            <SimulatorSection
              base={dash}
              onSimulated={(r) => setDash((prev) => (prev ? { ...prev, ...r } : r))}
              onSaved={loadDashboard}
            />
          )}
          {section === "doctor" && <CreditDoctorSection issues={issues} />}
          {section === "business" && <BusinessReadinessSection biz={biz} />}

          {/* Disclaimer */}
          <div className="mt-12 glass rounded-xl p-4 text-[11px] text-slate-400 leading-relaxed" data-testid="intel-disclaimer">
            <p className="font-bold text-slate-300 mb-1">Disclaimer</p>
            <p>{dash.disclaimer}</p>
          </div>
        </main>
      </div>

      <BookReviewDialog open={openBooking} onOpenChange={setOpenBooking} />
    </div>
  );
}

/* ============================================================== */
function DashboardSection({ dash, onBook, onNav }) {
  const band = readinessBand(dash.readiness_score || 0);
  return (
    <div className="space-y-6">
      {/* Hero — FundingIQ + Next Action */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass-strong rounded-3xl p-6 sm:p-8 relative overflow-hidden glow-amber" data-testid="hero-fundingiq">
          <div className="absolute top-0 right-0 h-40 w-40 rounded-full bg-amber-500/20 blur-3xl -translate-y-10 translate-x-10" />
          <div className="relative flex items-start justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 border border-amber-500/20 px-3 py-1 text-[10px] uppercase tracking-widest font-bold text-amber-400">
                <Sparkles size={11} /> FundingIQ™ Score
              </div>
              <div className="font-display font-black text-white mt-4 leading-none">
                <span className="text-7xl sm:text-8xl bg-gradient-to-r from-amber-300 via-amber-400 to-amber-600 bg-clip-text text-transparent">
                  <AnimatedNumber value={dash.fundingiq} />
                </span>
                <span className="text-slate-400 text-2xl ml-2 font-bold">/1000</span>
              </div>
              <p className="text-slate-300 mt-3 max-w-md">Credit Quack's proprietary blend of your readiness, business foundation, and live opportunity match rate.</p>
            </div>
            <div className="text-center" data-testid="readiness-ring-wrap">
              <ProgressRing value={dash.readiness_score} max={100} sublabel="Readiness" color={band.color} size={140} />
              <p className="mt-2 text-[11px] uppercase tracking-widest font-bold" style={{ color: band.color }} data-testid="readiness-band-label">
                {band.label}
              </p>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Approval Momentum</p>
              <p className="font-display text-2xl font-black mt-1 flex items-center gap-2" data-testid="momentum-label">
                {dash.approval_momentum === "Rising" && <TrendingUp size={20} className="text-emerald-400" />}
                {dash.approval_momentum}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Timeline</p>
              <p className="font-display text-lg font-bold mt-1 text-slate-200" data-testid="timeline-label">{dash.estimated_timeline}</p>
            </div>
            <Button onClick={onBook} className="bg-amber-500 hover:bg-amber-600 text-white h-11 rounded-xl px-5" data-testid="hero-book">
              Book Funding Review <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>

        <div className="glass-strong rounded-3xl p-6 relative" data-testid="hero-next-action">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 text-[10px] uppercase tracking-widest font-bold text-emerald-400">
            <Bot size={11} /> AI Next Action
          </div>
          <p className="text-slate-100 text-lg font-semibold mt-4 leading-snug">{dash.next_action}</p>
          <p className="text-xs text-slate-400 mt-6 mb-2 uppercase tracking-widest font-bold">Recent improvements</p>
          <ul className="space-y-1.5">
            {dash.recent_improvements.map((r, i) => (
              <li key={i} className="text-sm text-slate-300 flex gap-2" data-testid={`improvement-${i}`}>
                <TrendingUp size={14} className="text-emerald-400 mt-0.5 shrink-0" />{r}
              </li>
            ))}
          </ul>
          <Button variant="outline" onClick={() => onNav("opportunities")} className="mt-6 w-full bg-white/5 border-white/10 hover:bg-white/10 text-white h-10" data-testid="hero-see-ops">
            See all opportunities <ArrowUpRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total funding potential" value={dash.total_funding_potential} prefix="$" testid="kpi-potential" icon={DollarSign} accent="amber" />
        <KpiCard label="Available today" value={dash.available_today} prefix="$" testid="kpi-today" icon={Zap} accent="emerald" />
        <KpiCard label="Business readiness" value={dash.business_readiness.score} suffix="%" testid="kpi-biz" icon={Building2} accent="sky" />
        <KpiCard label="Top-match odds" value={Math.round((dash.top_matches[0]?.approval_probability || 0) * 100)} suffix="%" testid="kpi-topmatch" icon={Target} accent="violet" />
      </div>

      {/* Loan Programs Explorer CTA */}
      <Link
        to="/portal/funding/loan-programs"
        className="group block glass-strong rounded-3xl p-5 sm:p-6 border-2 border-emerald-400/40 hover:border-emerald-400/80 transition"
        data-testid="loan-programs-cta"
      >
        {/* Mobile: stacked layout */}
        <div className="sm:hidden">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest px-2.5 py-0.5">New</span>
            <span className="text-[10px] uppercase tracking-widest font-black text-emerald-400">23 real programs</span>
          </div>
          <h3 className="font-display text-lg font-black text-white leading-tight">
            Home, Auto & Boat Loan Programs
          </h3>
          <div className="mt-2 flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 px-2 py-1 text-[10px] font-black text-emerald-300 uppercase tracking-widest">🏡 13</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 px-2 py-1 text-[10px] font-black text-sky-300 uppercase tracking-widest">🚗 6</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 px-2 py-1 text-[10px] font-black text-blue-300 uppercase tracking-widest">⛵ 4</span>
          </div>
          <p className="text-xs text-slate-300 mt-2 leading-relaxed">
            FHA · VA · USDA · Jumbo · DSCR · HELOC · CU autos · Marine · Yacht — with your approval odds & docs list.
          </p>
          <div className="mt-3 flex items-center justify-between gap-2 rounded-2xl bg-emerald-500 text-white px-4 py-2.5 group-hover:bg-emerald-400 transition">
            <span className="text-sm font-black uppercase tracking-widest">Browse programs</span>
            <span className="text-lg">→</span>
          </div>
        </div>

        {/* Desktop / tablet: horizontal layout */}
        <div className="hidden sm:flex items-center gap-4">
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="h-11 w-11 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 flex items-center justify-center text-xl">🏡</span>
            <span className="h-11 w-11 rounded-2xl bg-sky-500/15 border border-sky-400/30 text-sky-300 flex items-center justify-center text-xl">🚗</span>
            <span className="h-11 w-11 rounded-2xl bg-blue-500/15 border border-blue-400/30 text-blue-300 flex items-center justify-center text-xl">⛵</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.28em] font-black text-emerald-400">New · 23 real programs</p>
            <h3 className="font-display text-2xl font-black text-white mt-0.5 leading-tight">
              Home, Auto & Boat Loan Programs Explorer
            </h3>
            <p className="text-sm text-slate-300 mt-1 max-w-2xl">
              FHA · VA · USDA · Conventional · Jumbo · DSCR · Bank-Statement · ITIN · HELOC · Reverse ·
              Credit-union autos · EV · Marine · Yacht · RV — with your approval odds, docs list, and state eligibility.
            </p>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white px-4 py-2 text-xs font-black uppercase tracking-widest group-hover:bg-emerald-400 transition shrink-0">
            Browse programs →
          </span>
        </div>
      </Link>

      {/* Top matches preview */}
      <div className="glass-strong rounded-3xl p-5 sm:p-6">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Top matches</p>
            <h3 className="font-display text-xl sm:text-2xl font-black leading-tight">Your best moves right now</h3>
          </div>
          <button onClick={() => onNav("opportunities")} className="text-xs sm:text-sm text-amber-400 hover:text-amber-300 font-semibold shrink-0" data-testid="see-all-opps">
            See all →
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {dash.top_matches.slice(0, 6).map((o) => (
            <OpportunityMini key={o.id} opp={o} />
          ))}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, prefix = "", suffix = "", icon: Icon, accent = "amber", testid }) {
  const glow = {
    amber: "from-amber-400/20 to-amber-600/0",
    emerald: "from-emerald-400/20 to-emerald-600/0",
    sky: "from-sky-400/20 to-sky-600/0",
    violet: "from-violet-400/20 to-violet-600/0",
  }[accent];
  const text = {
    amber: "text-amber-400", emerald: "text-emerald-400",
    sky: "text-sky-400", violet: "text-violet-400",
  }[accent];
  return (
    <div className="glass rounded-2xl p-5 relative overflow-hidden" data-testid={testid}>
      <div className={`absolute -top-10 -right-10 h-32 w-32 rounded-full bg-gradient-to-br ${glow} blur-2xl`} />
      <div className={`w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center ${text}`}>
        <Icon size={18} />
      </div>
      <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mt-3">{label}</p>
      <p className="font-display text-3xl font-black text-white mt-1">
        <AnimatedNumber value={value} prefix={prefix} suffix={suffix} />
      </p>
    </div>
  );
}

function OpportunityMini({ opp }) {
  const probPct = Math.round(opp.approval_probability * 100);
  const probColor = probPct >= 75 ? "text-emerald-400" : probPct >= 55 ? "text-amber-400" : probPct >= 35 ? "text-orange-400" : "text-red-400";
  return (
    <div className="glass rounded-2xl p-4 hover:bg-white/8 transition" data-testid={`mini-opp-${opp.id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">{opp.type}</p>
        <span className={`text-xs font-black ${probColor}`}>{probPct}%</span>
      </div>
      <p className="font-display font-bold text-white mt-1 leading-snug">{opp.name}</p>
      <p className="text-xs text-slate-400 mt-1">${opp.estimated_limit.toLocaleString()} est · {opp.time_to_fund_days}d funding</p>
    </div>
  );
}

/* ============================================================== */
function OpportunitiesSection({ opps, category, setCategory, tag, setTag, q, setQ, expanded, toggleExpand, compareIds, toggleCompare, runCompare, compareResult, clearCompare, onBook }) {
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search opportunities…"
            className="pl-10 h-11 bg-white/5 border-white/10 text-white placeholder:text-slate-500" data-testid="opps-search" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="category-filters">
        {CATEGORIES.map((c) => (
          <button key={c.key} onClick={() => setCategory(c.key)} data-testid={`cat-${c.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
              category === c.key ? "bg-amber-500 text-white border-amber-500" : "glass text-slate-300 border-white/10 hover:border-white/20"
            }`}>{c.label}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2" data-testid="tag-filters">
        {TAGS.map((t) => (
          <button key={t.key} onClick={() => setTag(t.key)} data-testid={`tag-${t.key}`}
            className={`px-3 py-1 rounded-full text-[11px] font-semibold border ${
              tag === t.key ? "bg-white text-slate-900 border-white" : "glass text-slate-400 border-white/10 hover:text-slate-200 hover:border-white/20"
            }`}>{t.label}</button>
        ))}
      </div>

      {compareIds.length > 0 && (
        <div className="glass-strong rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid="compare-bar">
          <ScaleIcon size={18} className="text-amber-400" />
          <span className="text-sm font-semibold">Comparing {compareIds.length} of 3</span>
          <div className="flex-1" />
          <Button onClick={runCompare} disabled={compareIds.length < 2} className="bg-amber-500 hover:bg-amber-600 h-9" data-testid="compare-run">Compare</Button>
          <Button variant="outline" onClick={clearCompare} className="bg-white/5 border-white/10 text-white h-9" data-testid="compare-clear">Clear</Button>
        </div>
      )}

      {compareResult && <CompareResult result={compareResult} />}

      <div className="grid md:grid-cols-2 gap-4" data-testid="opps-grid">
        {opps.map((o) => (
          <OpportunityCard key={o.id} opp={o} expanded={expanded.has(o.id)} onExpand={() => toggleExpand(o.id)}
            selected={compareIds.includes(o.id)} onSelect={() => toggleCompare(o.id)} onBook={onBook} />
        ))}
      </div>
      {opps.length === 0 && <div className="glass rounded-2xl p-10 text-center text-slate-400" data-testid="opps-empty">No matches — try widening filters.</div>}
    </div>
  );
}

function CompareResult({ result }) {
  return (
    <div className="glass-strong rounded-2xl p-5" data-testid="compare-result">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={16} className="text-amber-400" />
        <p className="text-[10px] uppercase tracking-widest font-bold text-amber-400">AI Comparison</p>
      </div>
      <p className="text-slate-100 text-lg leading-relaxed">{result.narrative}</p>
      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        {result.opportunities.map((o) => (
          <div key={o.id} className={`rounded-xl p-3 border ${o.id === result.recommendation ? "bg-amber-500/10 border-amber-500/50" : "glass border-white/10"}`}>
            {o.id === result.recommendation && <Badge className="bg-amber-500 text-white text-[9px] mb-1">Recommended</Badge>}
            <p className="font-bold text-sm text-white">{o.name}</p>
            <p className="text-xs text-slate-400 mt-1">${o.estimated_limit.toLocaleString()} · {Math.round(o.approval_probability*100)}% odds</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function OpportunityCard({ opp, expanded, onExpand, selected, onSelect, onBook }) {
  const pct = Math.round(opp.approval_probability * 100);
  const color = pct >= 75 ? "emerald" : pct >= 55 ? "amber" : pct >= 35 ? "orange" : "red";
  const colorHex = { emerald: "#10b981", amber: "#f59e0b", orange: "#f97316", red: "#ef4444" }[color];
  const bgColor = { emerald: "bg-emerald-500/10 text-emerald-300", amber: "bg-amber-500/10 text-amber-300", orange: "bg-orange-500/10 text-orange-300", red: "bg-red-500/10 text-red-300" }[color];
  return (
    <div className={`glass rounded-2xl p-5 relative overflow-hidden transition ${selected ? "ring-2 ring-amber-500" : ""}`} data-testid={`opp-card-${opp.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">{opp.type}</p>
          <p className="font-display text-xl font-black text-white mt-1 leading-tight">{opp.name}</p>
          <p className="text-xs text-slate-400 mt-1">{opp.issuer}</p>
        </div>
        <ProgressRing value={pct} max={100} size={76} stroke={7} sublabel="APPR" color={colorHex} />
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
        <Stat label="Est. limit" value={`$${opp.estimated_limit.toLocaleString()}`} />
        <Stat label="APR" value={opp.estimated_apr} />
        <Stat label="Fund in" value={`${opp.time_to_fund_days}d`} />
      </div>
      <p className="text-sm text-slate-300 mt-4">{opp.match_reason}</p>
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${bgColor}`}>{opp.approval_probability_label} odds</span>
        <span className="text-[10px] text-slate-400 font-semibold">AI confidence {(opp.ai_confidence * 100).toFixed(0)}%</span>
        <span className="text-[10px] text-slate-400 font-semibold">Apply: {opp.recommended_apply_date}</span>
      </div>
      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/10">
        <button onClick={onExpand} className="text-xs font-bold text-slate-300 hover:text-white inline-flex items-center gap-1" data-testid={`opp-expand-${opp.id}`}>
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {expanded ? "Hide" : "Why this?"}
        </button>
        <div className="flex-1" />
        <label className="flex items-center gap-1 text-[11px] font-semibold text-slate-400 cursor-pointer">
          <input type="checkbox" checked={selected} onChange={onSelect} data-testid={`opp-compare-${opp.id}`} /> Compare
        </label>
        {opp.apply_url && opp.apply_url !== "#" && (
          <a href={opp.apply_url} target="_blank" rel="noreferrer" className="text-xs font-bold text-sky-300 hover:text-sky-200 inline-flex items-center gap-1" data-testid={`opp-apply-${opp.id}`}>
            Apply <ArrowUpRight size={12} />
          </a>
        )}
      </div>
      {expanded && (
        <div className="mt-4 rounded-xl bg-black/40 border border-white/10 p-4 text-sm text-slate-200 space-y-2" data-testid={`opp-detail-${opp.id}`}>
          <div><span className="text-slate-400 font-semibold">Why it matches:</span> {opp.why}</div>
          <div><span className="text-slate-400 font-semibold">Rewards:</span> {opp.rewards}</div>
          <div><span className="text-slate-400 font-semibold">Annual fee:</span> ${opp.annual_fee}</div>
          <div><span className="text-slate-400 font-semibold">Difficulty:</span> {opp.difficulty}</div>
          <div className="pt-2">
            <Button onClick={onBook} size="sm" className="bg-amber-500 hover:bg-amber-600" data-testid={`opp-book-${opp.id}`}>
              Book a Funding Review <ArrowRight size={12} className="ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
function Stat({ label, value }) {
  return (
    <div className="glass rounded-lg p-2">
      <p className="text-[9px] uppercase tracking-widest font-bold text-slate-500">{label}</p>
      <p className="text-sm font-black text-white mt-0.5">{value}</p>
    </div>
  );
}

/* ============================================================== */
function ConciergeSection() {
  const [msgs, setMsgs] = useState([
    { role: "assistant", text: "Hey — I'm your AI Funding Concierge. Tell me what you need (e.g. \"I need $75K for equipment\" or \"I want to open a restaurant\") and I'll build you a personalized strategy." }
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [recs, setRecs] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [msgs, busy]);

  const send = async () => {
    if (!input.trim()) return;
    const question = input.trim();
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: question }]);
    setBusy(true);
    try {
      const { data } = await api.post("/funding/intel/concierge", { message: question, history: msgs.slice(-6) });
      const parts = [data.answer];
      if (data.strategy_steps?.length) {
        parts.push("\n\nStrategy:");
        data.strategy_steps.forEach((s, i) => parts.push(`${i + 1}. ${s}`));
      }
      setMsgs((m) => [...m, { role: "assistant", text: parts.join("\n") }]);
      setRecs(data.recommended_opportunity_ids || []);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: "Sorry, I had trouble processing that. Try again in a moment." }]);
    } finally { setBusy(false); }
  };

  const SUGGESTIONS = [
    "I need $75,000 for equipment",
    "I want to buy a truck for my business",
    "I need startup funding",
    "I want to open a restaurant",
    "What business credit card should I get first?",
  ];

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 glass-strong rounded-3xl p-0 overflow-hidden">
        <div className="p-5 border-b border-white/10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
            <Bot size={18} className="text-white" />
          </div>
          <div>
            <p className="font-display font-black">AI Funding Concierge</p>
            <p className="text-xs text-slate-400">Powered by Claude Sonnet 4.5 · Personalized to your profile</p>
          </div>
        </div>
        <div ref={scrollRef} className="p-5 space-y-3 max-h-[500px] min-h-[420px] overflow-y-auto" data-testid="concierge-messages">
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl p-3 text-sm whitespace-pre-wrap ${
                m.role === "user" ? "bg-amber-500 text-white" : "glass text-slate-100"
              }`} data-testid={`concierge-msg-${m.role}-${i}`}>{m.text}</div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start" data-testid="concierge-thinking">
              <div className="glass rounded-2xl p-3 text-sm text-slate-400 inline-flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                Thinking…
              </div>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-white/10">
          <div className="flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !busy && send()}
              placeholder="Ask anything about funding…"
              className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-slate-500" data-testid="concierge-input" />
            <Button onClick={send} disabled={busy} className="bg-amber-500 hover:bg-amber-600 h-10" data-testid="concierge-send"><Send size={16} /></Button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="glass-strong rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Try asking</p>
          <div className="mt-3 space-y-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => setInput(s)} className="w-full text-left glass rounded-xl p-3 text-sm text-slate-200 hover:bg-white/10 transition" data-testid={`concierge-suggest-${s.slice(0,10)}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        {recs.length > 0 && (
          <div className="glass-strong rounded-2xl p-5" data-testid="concierge-recs">
            <p className="text-[10px] uppercase tracking-widest font-bold text-amber-400">Concierge picks</p>
            <div className="mt-2 space-y-1 text-sm text-slate-200">
              {recs.map((id) => <div key={id} className="glass rounded-lg p-2 text-xs">{id}</div>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================== */
function SimulatorSection({ base, onSimulated, onSaved }) {
  const [inputs, setInputs] = useState({
    personal_credit_score: base.readiness_score >= 60 ? 720 : 620,
    monthly_revenue: 8000,
    time_in_business_months: 12,
    utilization_pct: 25,
    inquiries_last_12mo: 2,
    debt_monthly: 2000,
    cash_flow_monthly: 4000,
    existing_credit_limit_total: 20000,
    has_collections: false,
    has_late_payments: false,
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reportMeta, setReportMeta] = useState(null); // { source_hint, created_at }
  const [prefilled, setPrefilled] = useState(false);

  // On mount: peek at the latest credit report and auto-prefill inputs.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/funding/credit-report/me");
        if (data?.report) {
          const d = data.report.data || {};
          const scores = d.scores || [];
          const s = d.summary || {};
          const avgScore = scores.length
            ? Math.round(scores.reduce((a, x) => a + (x.score || 0), 0) / scores.length)
            : null;
          const patch = {};
          if (avgScore && avgScore >= 300 && avgScore <= 850) patch.personal_credit_score = avgScore;
          if (s.utilization_pct !== null && s.utilization_pct !== undefined) patch.utilization_pct = Math.min(100, Math.max(0, s.utilization_pct));
          if (s.hard_inquiries_24mo !== null && s.hard_inquiries_24mo !== undefined) patch.inquiries_last_12mo = Math.min(50, Math.max(0, Math.round(s.hard_inquiries_24mo / 2)));
          if (s.total_credit_limit) patch.existing_credit_limit_total = Math.round(s.total_credit_limit);
          if (s.collections > 0 || (d.collections && d.collections.length > 0)) patch.has_collections = true;
          if (Object.keys(patch).length > 0) {
            setInputs((prev) => ({ ...prev, ...patch }));
            setPrefilled(true);
          }
          setReportMeta({ source_hint: d.source_hint || d.bureau, created_at: data.report.created_at });
        }
      } catch { /* ignore — not a client, or no report */ }
    })();
  }, []);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/funding/intel/simulate", inputs);
      setResult(data);
      // Sync top-of-page readiness score + related KPIs with the calculator result
      onSimulated?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/funding/intel/save-profile", inputs);
      if (data?.dashboard) {
        setResult(data.dashboard);
        onSimulated?.(data.dashboard);
      }
      toast.success("Saved — your Readiness score is now anchored to these numbers.");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const S = (key, label, min, max, step = 1, format = (v) => v) => (
    <div key={key} className="glass rounded-xl p-4" data-testid={`sim-slider-${key}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-slate-300">{label}</p>
        <p className="text-sm font-black text-amber-400" data-testid={`sim-value-${key}`}>{format(inputs[key])}</p>
      </div>
      <input type="range" min={min} max={max} step={step} value={inputs[key]}
        onChange={(e) => setInputs({ ...inputs, [key]: parseFloat(e.target.value) })}
        className="w-full accent-amber-500" />
    </div>
  );

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="glass-strong rounded-3xl p-6 space-y-3" data-testid="simulator-inputs">
        <h3 className="font-display font-black text-xl">Simulator</h3>
        <p className="text-xs text-slate-400 mb-3">Drag to see how each change moves your Funding IQ. Hit <span className="text-amber-400 font-bold">Save</span> to anchor it to your profile.</p>
        {prefilled && reportMeta && (
          <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-200" data-testid="simulator-prefilled-chip">
            <p className="font-bold text-amber-300 flex items-center gap-1">
              <Sparkles size={12} /> Prefilled from your credit report
            </p>
            <p className="mt-0.5 opacity-90">Score, utilization, inquiries and limits came from your {reportMeta.source_hint || "report"} · {reportMeta.created_at ? new Date(reportMeta.created_at).toLocaleDateString() : ""}. Drag anything to model changes.</p>
          </div>
        )}
        {S("personal_credit_score", "Credit score", 300, 850, 1)}
        {S("monthly_revenue", "Monthly revenue", 0, 100000, 500, (v) => `$${v.toLocaleString()}`)}
        {S("time_in_business_months", "Time in business", 0, 120, 1, (v) => `${v} mo`)}
        {S("utilization_pct", "Utilization", 0, 100, 1, (v) => `${v}%`)}
        {S("debt_monthly", "Monthly debt", 0, 30000, 100, (v) => `$${v.toLocaleString()}`)}
        {S("cash_flow_monthly", "Cash flow", 0, 30000, 100, (v) => `$${v.toLocaleString()}`)}
        {S("existing_credit_limit_total", "Existing credit limits", 0, 200000, 500, (v) => `$${v.toLocaleString()}`)}
        <div className="space-y-2">
          <label className="flex items-center justify-between p-2 rounded-lg bg-white/5">
            <span className="text-xs">Has collections?</span>
            <Switch checked={inputs.has_collections} onCheckedChange={(v) => setInputs({ ...inputs, has_collections: v })} data-testid="sim-collections" />
          </label>
          <label className="flex items-center justify-between p-2 rounded-lg bg-white/5">
            <span className="text-xs">Recent late payments?</span>
            <Switch checked={inputs.has_late_payments} onCheckedChange={(v) => setInputs({ ...inputs, has_late_payments: v })} data-testid="sim-lates" />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button onClick={run} disabled={busy || saving} className="bg-amber-500 hover:bg-amber-600 h-11" data-testid="sim-run">
            {busy ? "Calculating…" : "Calculate"}
          </Button>
          <Button onClick={save} disabled={busy || saving} variant="outline"
            className="bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/40 text-emerald-200 h-11" data-testid="sim-save">
            {saving ? "Saving…" : "Save to profile"}
          </Button>
        </div>
      </div>

      <div className="lg:col-span-2 glass-strong rounded-3xl p-6 relative overflow-hidden">
        {!result ? (
          <div className="min-h-[300px] flex items-center justify-center text-slate-400 text-center">
            <div>
              <BarChart3 size={40} className="mx-auto text-slate-600 mb-3" />
              <p>Drag any slider then hit <strong className="text-amber-400">Recalculate</strong> to preview your future funding position.</p>
            </div>
          </div>
        ) : (
          <>
            <p className="text-[10px] uppercase tracking-widest font-bold text-amber-400">Simulated result</p>
            <div className="flex items-end gap-3 mt-2">
              <p className="font-display text-6xl font-black bg-gradient-to-r from-amber-300 to-emerald-400 bg-clip-text text-transparent" data-testid="sim-fundingiq">
                <AnimatedNumber value={result.fundingiq} />
              </p>
              <p className="text-slate-400 pb-2">/1000 FundingIQ</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-6">
              <KpiCard label="Total potential" value={result.total_funding_potential} prefix="$" icon={DollarSign} accent="amber" testid="sim-potential" />
              <KpiCard label="Available today" value={result.available_today} prefix="$" icon={Zap} accent="emerald" testid="sim-today" />
              <KpiCard label="Readiness" value={result.readiness_score} suffix="/100" icon={Gauge} accent="sky" testid="sim-readiness" />
              <KpiCard label="Top-match odds" value={Math.round((result.top_matches[0]?.approval_probability||0)*100)} suffix="%" icon={Target} accent="violet" testid="sim-topmatch" />
            </div>
            <div className="mt-6 glass rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Simulator says</p>
              <p className="text-slate-100 mt-1">{result.next_action}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ============================================================== */
function CreditDoctorSection({ issues }) {
  const priorityColor = { High: "text-red-400 border-red-500/40 bg-red-500/10", Medium: "text-amber-400 border-amber-500/40 bg-amber-500/10", Low: "text-sky-400 border-sky-500/40 bg-sky-500/10" };
  return (
    <div className="space-y-4">
      <div className="glass-strong rounded-3xl p-6 flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center">
          <HeartPulse size={22} className="text-white" />
        </div>
        <div>
          <h3 className="font-display text-2xl font-black">AI Credit Doctor</h3>
          <p className="text-sm text-slate-400">Detected {issues.length} issue{issues.length === 1 ? "" : "s"} affecting your approval odds.</p>
        </div>
      </div>
      {issues.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center" data-testid="doctor-empty">
          <CheckCircle2 size={40} className="mx-auto text-emerald-400 mb-3" />
          <p className="text-slate-200 font-semibold">Nothing critical — your profile is in strong shape.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {issues.map((i, idx) => (
            <div key={i.id} className="glass rounded-2xl p-5" data-testid={`doctor-issue-${i.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={18} className="text-red-400" />
                  <p className="font-display font-black text-white">{i.title}</p>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${priorityColor[i.priority]}`}>{i.priority}</span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="glass rounded-lg p-2">
                  <p className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Impact</p>
                  <p className="text-xs text-slate-200 mt-0.5">{i.impact}</p>
                </div>
                <div className="glass rounded-lg p-2">
                  <p className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Improvement</p>
                  <p className="text-xs text-slate-200 mt-0.5">{i.improvement_estimate}</p>
                </div>
              </div>
              <p className="text-sm text-slate-300 mt-4"><span className="font-bold text-amber-400">Action:</span> {i.action}</p>
              <div className="text-xs text-slate-500 mt-3 flex items-center gap-1"><Clock size={12} /> Est. timeline: ~{i.timeline_days} days</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================== */
function BusinessReadinessSection({ biz }) {
  if (!biz) return <div className="glass rounded-2xl p-10 text-slate-400 text-center">Loading…</div>;
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 glass-strong rounded-3xl p-6 relative overflow-hidden text-center">
        <div className="absolute -top-20 -left-20 h-40 w-40 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute -bottom-20 -right-20 h-40 w-40 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="relative">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Business Readiness</p>
          <div className="my-6">
            <ProgressRing value={biz.score} max={100} size={200} stroke={14} sublabel="Complete" color="#10b981" />
          </div>
          <p className="text-sm text-slate-300">{biz.completed} of {biz.total} foundation items complete.</p>
        </div>
      </div>
      <div className="lg:col-span-2 glass-strong rounded-3xl p-6" data-testid="biz-missing-list">
        <h3 className="font-display font-black text-xl mb-4">What's missing</h3>
        {biz.missing.length === 0 ? (
          <p className="text-slate-300">Everything is in place — your business foundation is complete.</p>
        ) : (
          <ul className="space-y-2">
            {biz.missing.map((m, i) => (
              <li key={i} className="glass rounded-xl p-3 flex items-center gap-2 text-slate-200" data-testid={`biz-miss-${i}`}>
                <span className="w-6 h-6 rounded-full border border-slate-600 shrink-0" />
                <span className="text-sm">{m}</span>
              </li>
            ))}
          </ul>
        )}
        <a href="/portal/funding" className="mt-6 inline-flex items-center gap-1 text-amber-400 font-bold text-sm hover:text-amber-300" data-testid="biz-fix-link">
          Fix these in the classic Funding Center <ChevronRight size={14} />
        </a>
      </div>
    </div>
  );
}

/* ============================================================== */
function BookReviewDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({
    desired_amount: 25000, business_name: "", business_type: "", monthly_revenue: 0,
    time_in_business_months: 0, funding_purpose: "credit_cards",
    current_credit_score: 640, current_credit_cards: "", recent_denials: false, notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/client/funding/book-review", form);
      toast.success("Request received — your specialist will follow up in chat.");
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto bg-slate-900 text-white border-white/10" data-testid="intel-book-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Book a Funding Review</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount ($)</Label><Input type="number" value={form.desired_amount} onChange={(e) => setForm({ ...form, desired_amount: parseFloat(e.target.value || 0) })} className="bg-white/5 border-white/10" /></div>
            <div><Label>Score</Label><Input type="number" value={form.current_credit_score} onChange={(e) => setForm({ ...form, current_credit_score: parseInt(e.target.value || 0) })} className="bg-white/5 border-white/10" /></div>
            <div><Label>Monthly revenue</Label><Input type="number" value={form.monthly_revenue} onChange={(e) => setForm({ ...form, monthly_revenue: parseFloat(e.target.value || 0) })} className="bg-white/5 border-white/10" /></div>
            <div><Label>Time in business (mo)</Label><Input type="number" value={form.time_in_business_months} onChange={(e) => setForm({ ...form, time_in_business_months: parseInt(e.target.value || 0) })} className="bg-white/5 border-white/10" /></div>
          </div>
          <div><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white/5 border-white/10" /></div>
        </div>
        <div className="flex justify-end pt-3">
          <Button onClick={submit} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="intel-book-submit">
            {busy ? "Sending…" : "Send request"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
